import BasicLayout from "@main/layouts/BasicLayout/index.vue";
import { useAppTabsStore } from "@main/stores/useAppTabsStore";
import { useUserRoleStore } from "@main/stores/useUserRoleStore";
import { message } from "ant-design-vue";
import type { Router } from "vue-router";
import { NProgressClose, NProgressStart } from "@/utils/nprogress";
import { appRoutes } from "./apps";
import { generateDynamicRoutes } from "./dynamicRoutes";
import { menuRoutes } from "./menuRoutes";

const collectRoutePaths = (routes: Parameters<typeof generateDynamicRoutes>[0]): Set<string> => {
	const pathSet = new Set<string>();

	const traverse = (items: Parameters<typeof generateDynamicRoutes>[0]) => {
		for (const item of items) {
			if (item.path) {
				pathSet.add(item.path);
			}

			if (item.children && item.children.length > 0) {
				traverse(item.children);
			}
		}
	};

	traverse(routes);

	return pathSet;
};

const allAppRoutePathSet = collectRoutePaths(appRoutes);

// 路由白名单，这些页面无需登录即可访问
const WHITELIST = ["/login"];

/**
 * 设置动态路由
 * 根据用户权限动态加载和过滤路由
 *
 * @param router Vue Router 实例
 */
export const setupDynamicRoutes = async (router: Router) => {
	const userRoleStore = useUserRoleStore();

	// 检查是否已加载菜单权限
	if (userRoleStore.menuList.length === 0) {
		await userRoleStore.getAdminMenuList();
	}

	// 生成经过权限过滤的动态路由（仅过滤 appRoutes 部分）
	const filteredAppRoutes = generateDynamicRoutes(appRoutes, userRoleStore.menuList);

	// 将 menuRoutes 中的 desktopCenter 子路由替换为过滤后的 appRoutes
	// 其余菜单路由（AppCenter、MarketingCenter 等）保持不变
	const mergedMenuRoutes = menuRoutes.map((route) => {
		if (route.name === "DesktopCenter") {
			return { ...route, children: filteredAppRoutes };
		}

		return route;
	});

	// 找到根路由
	const rootRoute = router.getRoutes().find((route) => route.name === "Root");

	if (rootRoute) {
		// 替换根路由的子路由
		router.removeRoute("Root");
		router.addRoute({
			path: "/",
			name: "Root",
			component: BasicLayout,
			redirect: "/desktopCenter",
			children: mergedMenuRoutes,
		} as Parameters<Router["addRoute"]>[0]);
	}
};

/**
 * 设置基础路由守卫
 * 负责：
 * 1. 已登录用户访问登录页时重定向到首页
 * 2. 允许访问 404 页面
 * 3. 未登录用户访问受保护页面时重定向到登录页
 * 4. 加载动态路由权限
 *
 * @param router Vue Router 实例
 */
const setupRoutes = (router: Router) => {
	// 标记是否已初始化动态路由
	let isDynamicRoutesInitialized = false;

	router.beforeEach(async (to, from) => {
		const appTabsStore = useAppTabsStore();
		const token = localStorage.getItem("token");

		if (to.path === "/desktopCenter" && from.path === "/noPermission") {
			appTabsStore.clearTabs();
		}

		// 已登录用户首次导航时，初始化动态路由
		if (token && !isDynamicRoutesInitialized) {
			isDynamicRoutesInitialized = true;
			await setupDynamicRoutes(router);

			// 重新路由到当前目标，以应用新的路由配置
			return to.fullPath;
		}

		// 已登录用户试图访问登录页时，重定向到首页
		if (to.path === "/login" && token) {
			return "/desktopCenter";
		}

		// 404 页面始终允许访问
		if (to.name === "NotFound") {
			if (allAppRoutePathSet.has(to.path)) {
				appTabsStore.clearTabs();

				return "/noPermission";
			}

			return true;
		}

		// 未登录用户访问非白名单页面时，提示并重定向到登录页
		if (!token && !WHITELIST.includes(to.path)) {
			message.warning("请先登录");

			return "/login";
		}

		return true;
	});
};

/**
 * 设置页面加载进度条
 * 在路由导航开始时启动进度条，导航完成时关闭
 *
 * @param router Vue Router 实例
 */
const setupNProgress = (router: Router) => {
	// 路由导航前：启动进度条
	router.beforeEach((_to, _from, next) => {
		NProgressStart();
		next();
	});

	// 路由导航后：关闭进度条
	router.afterEach(() => {
		NProgressClose();
	});
};

/**
 * 设置页面标题
 * 根据路由元数据更新浏览器标签页标题
 * 优先级：路由元数据中的 title > 项目名称
 *
 * @param router Vue Router 实例
 */
const setupTitle = (router: Router) => {
	// 从环境变量获取项目名称作为默认标题
	const projectName = import.meta.env.VITE_APP_TITLE;

	router.afterEach((to) => {
		// 优先使用路由的元数据标题，否则使用项目名称
		const title = to.meta.title || projectName;

		if (title) {
			document.title = title;
		}
	});
};

/**
 * 生成返回目标的存储键
 * 键格式：`${应用名}:back:${路由路径}`
 * 用于在 sessionStorage 中存储页面返回信息
 *
 * @param path 路由路径
 * @returns 存储键
 */
const getBackStorageKey = (path: string) => {
	const pathSegments = path.split("/").filter(Boolean);
	// 从路径第二段提取应用名（如 /app/OaOffice/... 中的 OaOffice）
	const prefix = pathSegments.length > 1 ? pathSegments[1] : "app";

	return `${prefix}:back:${path}`;
};

/**
 * 从路由路径中提取应用名称
 * 应用名称是路径的第二段（如 /app/OaOffice/... 中的 OaOffice）
 *
 * @param path 路由路径
 * @returns 应用名称，不存在时返回空字符串
 */
const getAppSegment = (path: string) => {
	const pathSegments = path.split("/").filter(Boolean);

	return pathSegments.length > 1 ? pathSegments[1] : "";
};

/**
 * 设置页面返回追踪
 * 记录用户的页面导航历史，以便实现智能返回功能
 *
 * 追踪规则：
 * 1. 忽略初始路由和登录页导航
 * 2. 忽略刷新（路径相同）
 * 3. 只追踪同一应用内的导航（如在 OaOffice 应用内部切换页面）
 * 4. 将返回信息存储到 sessionStorage
 *
 * @param router Vue Router 实例
 */
const setupPageBackTracking = (router: Router) => {
	router.afterEach((to, from) => {
		// 跳过初始导航（from 路由不存在）
		if (from.matched.length === 0) {
			return;
		}

		// 跳过从登录页的导航
		if (from.path === "/login") {
			return;
		}

		// 跳过路径相同的导航（如页面刷新）
		if (to.path === from.path) {
			return;
		}

		// 提取目标和来源的应用名称
		const toApp = getAppSegment(to.path);
		const fromApp = getAppSegment(from.path);

		// 只追踪同应用内的导航，跨应用切换时不记录
		if (!toApp || !fromApp || toApp !== fromApp) {
			return;
		}

		// 将来源页面记录到 sessionStorage，供返回功能使用
		const key = getBackStorageKey(to.path);

		sessionStorage.setItem(key, from.fullPath);
	});
};

/**
 * 组织并初始化所有路由守卫
 * 依次注册：进度条、页面标题、基础权限、页面返回追踪
 *
 * @param router Vue Router 实例
 */
const setupGuards = (router: Router) => {
	// 注册进度条守卫
	setupNProgress(router);
	// 注册页面标题守卫
	setupTitle(router);
	// 注册权限和重定向守卫
	setupRoutes(router);
	// 注册页面返回追踪守卫
	setupPageBackTracking(router);
};

export default setupGuards;
